import React, { useCallback } from 'react';
import { BenchmarkTypeDropdown, SliderToggle, BenchmarkSelector, LearnMore } from 'venn-components';
import type { DropMenuItem } from 'venn-ui-kit';
import { Label, Icon, CheckboxWrapper, ShimmerBlock, BENCHMARK_FAQ_HREF } from 'venn-ui-kit';
import type { CustomBenchmarkTypeEnum } from 'venn-api';
import type { AnalysisSubject } from 'venn-utils';
import {
  withSuspense,
  hasDisabledIndividualBenchmark,
  hasDisabledRelativeToBenchmark,
  hasDisabledBenchmark,
} from 'venn-utils';
import { Section, FlexHeader, GapWrapper, Flex, Text, Message, PanelLabel } from '../../../shared';
import styled from 'styled-components';
import { constSelector, useRecoilState, useRecoilValue } from 'recoil';
import {
  rightPanelBenchmarkConfig,
  rightPanelIsLinked,
  blockCanHideBenchmark,
  viewHideBenchmark,
  selectedBlockSettings,
  blockInfoGraphicType,
  selectedBlockIdState,
} from 'venn-state';

interface BenchmarkSectionProps {
  readonly?: boolean;
}

/**
 * @deprecated this component will soon be replaced in Input Management V2.
 * If you make changes here, consider making similar changes to {@link AnalysisBlockTabV2}.
 */
const BenchmarkSection = ({ readonly }: BenchmarkSectionProps) => {
  const blockSettings = useRecoilValue(selectedBlockSettings);

  const linked = useRecoilValue(rightPanelIsLinked);
  const [benchmarkConfig, setBenchmarkConfig] = useRecoilState(rightPanelBenchmarkConfig);
  const selectedBlockId = useRecoilValue(selectedBlockIdState);
  const infoGraphicType = useRecoilValue(
    selectedBlockId ? blockInfoGraphicType(selectedBlockId) : constSelector(undefined),
  );

  const { subject, type, relative } = benchmarkConfig;

  const onToggleRelative = useCallback(() => {
    setBenchmarkConfig((current) => ({
      ...current,
      relative: !current.relative,
    }));
  }, [setBenchmarkConfig]);

  const onChangeBenchmarkType = useCallback(
    (item: DropMenuItem<CustomBenchmarkTypeEnum>) => {
      setBenchmarkConfig((currentValue) => {
        const newType = item.value;
        if (currentValue.type === newType) {
          // no-op when type is effectively unchanged
          return currentValue;
        }
        return {
          type: newType,
          subject: undefined,
          relative: false,
        };
      });
    },
    [setBenchmarkConfig],
  );

  const onChangeGlobalBenchmark = useCallback(
    (benchmark?: AnalysisSubject) => {
      setBenchmarkConfig((current) => ({
        type: current.type,
        subject: benchmark,
        relative: benchmark === undefined ? false : current.relative,
      }));
    },
    [setBenchmarkConfig],
  );

  const blockTypeWithDisabledIndividualBenchmark = hasDisabledIndividualBenchmark(blockSettings?.customBlockType);
  const blockTypeWithDisabledRelativeToBenchmark = hasDisabledRelativeToBenchmark(blockSettings?.customBlockType);
  const blockTypeWithDisabledBenchmark = hasDisabledBenchmark(blockSettings?.customBlockType, infoGraphicType);

  const nonCommonBenchmark = type === 'COMMON' && !subject;
  const nonIndividualBenchmark = type === 'INDIVIDUAL' && blockTypeWithDisabledIndividualBenchmark;

  const tooltipMessage = nonCommonBenchmark
    ? 'Select a benchmark to enable benchmark-relative analysis'
    : blockTypeWithDisabledRelativeToBenchmark
      ? 'Relative to benchmark disabled for this block'
      : undefined;

  if (blockTypeWithDisabledBenchmark) {
    return null;
  }

  return (
    <Section>
      <FlexHeader>Benchmarks</FlexHeader>
      <GapWrapper>
        <Flex>
          <div>
            <BenchmarkTypeDropdown
              onlyValue={readonly}
              onChangeBenchmarkType={onChangeBenchmarkType}
              customBenchmarkType={type}
            />
          </div>
          {(type === 'COMMON' || type === 'INDIVIDUAL') && (
            <div>
              <PanelLabel>Relative:</PanelLabel>

              {readonly ? (
                <Text>{relative ? 'ON' : 'OFF'}</Text>
              ) : (
                <SliderToggle
                  disabled={nonCommonBenchmark || nonIndividualBenchmark || blockTypeWithDisabledRelativeToBenchmark}
                  toggled={relative} // always show OFF for blocks that relative to benchmark is disabled
                  onToggle={onToggleRelative}
                  tooltipMessage={tooltipMessage}
                  tooltipMaxWidth={130}
                />
              )}
            </div>
          )}
        </Flex>
        {relative && blockTypeWithDisabledRelativeToBenchmark && linked && (
          <Message>
            <Icon type="exclamation-circle" prefix="far" />
            <Text>Relative to benchmark is not supported for this block type.</Text>
          </Message>
        )}
        {type === 'INDIVIDUAL' &&
          (blockTypeWithDisabledIndividualBenchmark ? (
            <Message>
              <Icon type={linked ? 'exclamation-circle' : 'exclamation-triangle'} prefix="far" />
              <Text>Individual benchmarks will not be shown in this block. Please select common benchmark.</Text>
            </Message>
          ) : (
            <StyledLinkWrapper>
              Set individual benchmarks in Data Library <LearnMore href={BENCHMARK_FAQ_HREF} />
            </StyledLinkWrapper>
          ))}
      </GapWrapper>
      <GapWrapper>
        {type === 'COMMON' &&
          (readonly ? (
            <>
              <Label>Benchmark Name:</Label>
              <Text>{subject?.name}</Text>
            </>
          ) : (
            <BenchmarkWrapper>
              <BenchmarkSelector
                value={subject}
                onChange={onChangeGlobalBenchmark}
                disabled={false}
                smallScreen
                usePortal
                tooltipPosition={{
                  top: -130,
                  left: -290,
                }}
              />
            </BenchmarkWrapper>
          ))}
      </GapWrapper>

      {selectedBlockId && <HideBenchmarkSection selectedBlockId={selectedBlockId} />}
    </Section>
  );
};

const HideBenchmarkSection = ({ selectedBlockId }: { selectedBlockId: string }) => {
  const [hideBenchmark = false, setHideBenchmark] = useRecoilState(viewHideBenchmark(selectedBlockId));

  const canHideBenchmark = useRecoilValue(blockCanHideBenchmark(selectedBlockId));
  if (!canHideBenchmark) return null;

  return (
    <GapWrapper>
      <CheckboxWrapper checked={hideBenchmark} onChange={() => setHideBenchmark(!hideBenchmark)} justify="left">
        Hide Benchmark
      </CheckboxWrapper>
    </GapWrapper>
  );
};

const StyledLinkWrapper = styled.div`
  padding-top: 10px;
`;

const BenchmarkWrapper = styled.div`
  > div > div > div > div {
    z-index: inherit;
  }
`;

export default withSuspense(<ShimmerBlock />, BenchmarkSection);
